import { useState, useEffect } from 'react';

/**
 * 🔍 useDetectMyRole - Smart Role Detection Hook
 * 
 * A sleek and efficient hook that detects user role from sessionStorage
 * with automatic updates and robust error handling.
 * 
 * @returns {Object} { role, isLoading, error, refetch }
 * 
 * Usage:
 * const { role, isLoading, error } = useDetectMyRole();
 * // role: 'admin' | 'supervisor' | 'user' | null
 */
const useDetectMyRole = () => {
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const extractRole = () => {
    try {
      setIsLoading(true);
      setError(null);

      // 🔍 Extract staff_info from sessionStorage
      const staffInfoString = sessionStorage.getItem('staff_info');
      
      if (!staffInfoString) {
        setRole(null);
        return;
      }

      // 🔧 Parse the JSON data
      const staffInfo = JSON.parse(staffInfoString);
      
      // 🎯 Extract role array and get first role
      const userRoles = staffInfo?.role;
      
      if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
        setRole(null);
        return;
      }

      // 🚀 Return the primary role (first in array)
      const primaryRole = userRoles[0];
      
      // ✅ Validate against expected roles
      const validRoles = ['admin', 'supervisor', 'user'];
      if (validRoles.includes(primaryRole)) {
        setRole(primaryRole);
      } else {
        setRole(null);
        setError(`Invalid role detected: ${primaryRole}`);
      }

    } catch (err) {
      console.error('🚨 useDetectMyRole Error:', err);
      setError('Failed to parse staff information');
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔄 Listen for storage changes (when user logs in/out)
  useEffect(() => {
    // Initial load
    extractRole();

    // 👂 Listen for storage changes
    const handleStorageChange = (event) => {
      if (event.key === 'staff_info') {
        extractRole();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // 🧹 Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 🔄 Manual refetch function
  const refetch = () => {
    extractRole();
  };

  return {
    role,           // 'admin' | 'supervisor' | 'user' | null
    isLoading,      // boolean
    error,          // string | null
    refetch         // function to manually refresh role
  };
};

export default useDetectMyRole; 